// ═══════════════════════════════════════════════════════════════════════
// SHARED MEETING DOCUMENT BUILDERS — Notice, Minutes, Attendance Sheet,
// CTC, Shorter Notice Consent. Used from BOTH Event Filings' Documents
// tab (live component state) and the Statutory Register's Documents tab
// (fetched via GET /api/mca/meetings/:id/documents-data) — same output
// either way, since both ultimately read from the same mca_meetings row
// and the same linked Event Filing cases. Pure functions: given the same
// inputs, always the same HTML.
// ═══════════════════════════════════════════════════════════════════════

export function textToHtml(text) {
  return (text || '')
    .split('\n\n')
    .map(para => `<p>${para.split('\n').map(l => l.replace(/&/g, '&amp;').replace(/</g, '&lt;')).join('<br/>')}</p>`)
    .join('');
}

export function buildWordDoc(title, bodyHtml) {
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4; margin: 72pt 72pt 72pt 90pt; }
  body { font-family: "Plus Jakarta Sans", sans-serif; font-size: 12pt; text-align: justify; color: #000; }
  p { margin: 0 0 6pt 0; line-height: 1.35; text-align: justify; }
  h1 { font-size: 14pt; font-weight: bold; text-transform: uppercase; text-align: center; margin: 0 0 4pt 0; }
  h2 { font-size: 12pt; font-weight: bold; text-align: center; margin: 0 0 12pt 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 12pt; }
  td, th { border: 1pt solid #000; padding: 6pt 8pt; font-size: 10pt; vertical-align: top; }
  th { font-weight: bold; text-align: center; }
  tr.sig-row { height: 45pt; }
</style></head>
<body>${bodyHtml}</body></html>`;
}

export function downloadWord(filename, bodyHtml) {
  const html = buildWordDoc(filename, bodyHtml);
  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename.replace(/\s+/g, '_')}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function formatTime12h(hhmm) {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

const ORDINAL = n => n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';

// ── QUORUM (Sec 174 for BM, Sec 103 for GM) ─────────────────────────────
export function boardQuorum(totalDirectors) {
  if (!totalDirectors) return 2;
  return Math.max(2, Math.ceil(totalDirectors / 3));
}
export function generalMeetingQuorum(companyType, totalMembers) {
  const isPublic = /public/i.test(companyType || '');
  if (!isPublic) return 2;
  if (totalMembers <= 1000) return 5;
  if (totalMembers <= 5000) return 15;
  return 30;
}

// ── VOTING (Sec 114) ─────────────────────────────────────────────────────
export function voteWeight(isBM, personName, shareholders) {
  if (isBM) return 1;
  const sh = (shareholders || []).find(s => s.member_name === personName);
  return sh ? Number(sh.no_of_shares) || 0 : 0;
}
export function tallyVotes(votes, presentRoster, isBM, shareholders) {
  let forWeight = 0, againstWeight = 0, abstainWeight = 0;
  (presentRoster || []).forEach(name => {
    const w = voteWeight(isBM, name, shareholders);
    const v = (votes || {})[name];
    if (v === 'for') forWeight += w;
    else if (v === 'against') againstWeight += w;
    else if (v === 'abstain') abstainWeight += w;
  });
  return { forWeight, againstWeight, abstainWeight };
}
export function evaluateResolution(resolutionType, forWeight, againstWeight) {
  if (forWeight + againstWeight <= 0) return null;
  if (resolutionType === 'SPECIAL') return forWeight >= 3 * againstWeight;
  return forWeight > againstWeight;
}

// ── NOTICE PERIOD — BM (Sec 173(3)) is a flat minimum, plain day count.
// GM (Sec 101(1)) is "21 CLEAR days" — excludes both the day notice was
// sent and the day of the meeting, hence the extra -1 for GM only. Known
// simplification: doesn't add the extra 48 hours for postal notice
// (notice mode isn't tracked). ──────────────────────────────────────────
export function noticeGapDays(type, noticeDate, meetingDate) {
  if (!noticeDate || !meetingDate) return null;
  const raw = Math.round((new Date(meetingDate) - new Date(noticeDate)) / 86400000);
  return type === 'BM' ? raw : raw - 1;
}
export function requiredNoticeDays(type) { return type === 'BM' ? 7 : 21; }
export function meetingLabel(type) { return type === 'BM' ? 'Board Meeting' : type === 'AGM' ? 'Annual General Meeting' : 'Extraordinary General Meeting'; }

// ── AGENDA — every resolution across every Event Filing case sharing
// this meeting_id (the FULL agenda, not one case's slice), ordered by
// case id then position within each case — same ordering EventFilings.js
// uses for its own live-state version of this. `cases` here is the array
// shape GET /api/mca/meetings/:id/documents-data and GET
// /api/mca/meetings/:id/usage both already return: [{id, title,
// resolutions}]. ─────────────────────────────────────────────────────────
export function buildAgendaItems(cases, meetingId) {
  const sorted = [...(cases || [])].sort((a, b) => a.id - b.id);
  const items = [];
  sorted.forEach(c => {
    (c.resolutions || []).forEach(r => {
      if (r.meeting_id !== meetingId) return;
      const isFinal = r.manually_edited || r.status === 'Passed' || r.status === 'Rejected';
      items.push({ resolution: r, caseTitle: c.title, isFinal, text: isFinal ? (r.rendered_resolution || '').trim() : '' });
    });
  });
  return items.map((it, i) => ({ ...it, itemNo: i + 1 }));
}

// ── NOTICE ────────────────────────────────────────────────────────────
export function buildNoticeHtml({ companyName, type, md, items, shorter, serial }) {
  const isBM = type === 'BM';
  const noticePeriodLine = isBM
    ? (shorter
      ? 'This notice is issued on shorter notice, pursuant to the consent accorded by the Directors under Section 173(3) of the Companies Act, 2013.'
      : 'Notice is hereby given at least seven days in advance, as required under Section 173(3) of the Companies Act, 2013.')
    : (shorter
      ? 'This notice is issued on shorter notice, pursuant to the consent accorded by the Members under Section 101(1) of the Companies Act, 2013.'
      : 'Notice is hereby given at least twenty-one clear days in advance, as required under Section 101(1) of the Companies Act, 2013.');
  const vcLine = isBM
    ? '<p style="margin-top:8pt;">Any Director unable to attend in person may participate through Video Conferencing or Other Audio-Visual Means, subject to prior intimation to the Company.</p>'
    : '';
  const gmBoilerplate = !isBM ? `
    <p style="margin-top:14pt;">A member entitled to attend and vote is entitled to appoint a proxy to attend and vote instead of himself, and the proxy need not be a member of the Company. Proxy forms, in order to be effective, must be received at the registered office not less than 48 hours before the meeting.</p>
    <p style="margin-top:8pt;">Voting on the resolution(s) set out in this notice will be conducted by poll.</p>
  ` : '';
  return `
    <h1>${companyName}</h1>
    <h2>Notice of ${serial ? `the ${serial}${ORDINAL(serial)} ` : ''}${meetingLabel(type)}</h2>
    <table><tbody>
      <tr><td>Date</td><td>${md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN') : '—'}</td></tr>
      <tr><td>Time</td><td>${formatTime12h(md.meeting_time)}</td></tr>
      <tr><td>Venue</td><td>${md.venue || '—'}</td></tr>
      ${md.chairperson ? `<tr><td>Chairperson</td><td>${md.chairperson}</td></tr>` : ''}
    </tbody></table>
    <p style="margin-top:14pt;">${noticePeriodLine}</p>
    ${vcLine}
    <p class="clause-heading" style="margin-top:14pt;">${isBM ? 'Agenda' : 'Special Business'}</p>
    <ol>${items.map(i => `<li>${i.resolution.resolution_title}</li>`).join('')}</ol>
    ${!isBM ? items.filter(i => i.resolution.explanatory_statement?.trim()).map(i => `
      <p class="clause-heading" style="margin-top:14pt;">Explanatory Statement — Item ${i.itemNo} (Pursuant to Section 102 of the Companies Act, 2013)</p>
      ${textToHtml(i.resolution.explanatory_statement)}
    `).join('') : ''}
    ${gmBoilerplate}
    <p style="margin-top:24pt;">By Order of the Board<br/>For ${companyName}</p>
  `;
}

// ── MINUTES ───────────────────────────────────────────────────────────
export function buildMinutesHtml({ companyName, type, md, items, directors, shareholders, companyType, extras, serial }) {
  const isBM = type === 'BM';
  const attendees = md.attendees || [];
  const attendeeList = attendees.join(', ') || '—';
  const roster = isBM ? directors.map(d => d.director_name) : shareholders.map(s => s.member_name);
  const absentees = roster.filter(n => !attendees.includes(n));
  const quorumNeeded = isBM ? boardQuorum(directors.length) : generalMeetingQuorum(companyType, shareholders.length);
  const extrasSafe = extras || {};

  const votingNarrative = items.map(i => {
    const votes = i.resolution.votes || {};
    const { forWeight, againstWeight, abstainWeight } = tallyVotes(votes, attendees, isBM, shareholders);
    const outcome = i.resolution.status === 'Passed'
      ? (againstWeight === 0 ? 'passed unanimously' : (i.resolution.resolution_type === 'SPECIAL' ? 'passed by the requisite supermajority' : 'passed by requisite majority'))
      : i.resolution.status === 'Rejected' ? 'not passed' : 'result pending';
    return `
      <p class="clause-heading" style="margin-top:14pt;">Item No. ${i.itemNo}: ${i.resolution.resolution_title}</p>
      ${i.isFinal ? textToHtml(i.text) : `<p><em>${i.resolution.status === 'Rejected' || i.resolution.status === 'Passed' ? '' : 'Draft — resolution not yet marked Passed.'}</em></p>`}
      ${(forWeight + againstWeight) > 0 ? `<p style="margin-top:6pt;">Voting by poll — For: ${forWeight}, Against: ${againstWeight}, Abstained: ${abstainWeight}. The resolution ${outcome}.</p>` : ''}
    `;
  }).join('');

  return `
    <h1>${companyName}</h1>
    <h2>Minutes of ${serial ? `the ${serial}${ORDINAL(serial)} ` : ''}${meetingLabel(type)}</h2>
    <table><tbody>
      <tr><td>Date</td><td>${md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN') : '—'}</td></tr>
      <tr><td>Time</td><td>${formatTime12h(md.meeting_time)}</td></tr>
      <tr><td>Venue</td><td>${md.venue || '—'}</td></tr>
      <tr><td>Chairperson</td><td>${md.chairperson || '—'}</td></tr>
      <tr><td>Quorum</td><td>${attendees.length} present, ${quorumNeeded} required — quorum ${attendees.length >= quorumNeeded ? 'met' : 'not met'}</td></tr>
      <tr><td>${isBM ? 'Directors' : 'Members'} Present</td><td>${attendeeList}</td></tr>
      ${absentees.length > 0 ? `<tr><td>Leave of Absence</td><td>${absentees.join(', ')}</td></tr>` : ''}
      ${!isBM ? `<tr><td>Chairman's Remarks</td><td>${extrasSafe.chairman_remarks || '—'}</td></tr>` : ''}
      ${!isBM && extrasSafe.scrutinizer_name ? `<tr><td>Scrutinizer</td><td>${extrasSafe.scrutinizer_name}</td></tr>` : ''}
    </tbody></table>
    ${isBM ? `<p style="margin-top:14pt;">The minutes of the preceding meeting were confirmed and signed.</p>
    <p style="margin-top:6pt;">${extrasSafe.interest_disclosure?.trim() || 'None of the Directors were concerned or interested, financially or otherwise, in any of the resolutions considered at the meeting.'}</p>` : ''}
    ${votingNarrative}
    <p style="margin-top:24pt;">Confirmed and signed.<br/><br/>_____________________<br/>Chairperson</p>
  `;
}

// ── ATTENDANCE SHEET ─────────────────────────────────────────────────
export function buildAttendanceHtml({ companyName, type, md, directors, shareholders }) {
  const isBM = type === 'BM';
  const roster = isBM ? directors : shareholders;
  const attendees = md.attendees || [];
  const mode = md.venue_mode === 'vc' ? 'VC' : 'Physical';
  const rows = (roster || []).map(p => isBM
    ? `<tr><td>${p.director_name}</td><td>${p.din || '—'}</td><td style="text-align:center;">${attendees.includes(p.director_name) ? mode : '—'}</td><td style="text-align:center;">${attendees.includes(p.director_name) ? 'Present' : 'Absent'}</td><td></td></tr>`
    : `<tr><td>${p.member_name}</td><td>${p.folio_number || p.dp_id || '—'}</td><td style="text-align:center;">${p.no_of_shares || 0}</td><td style="text-align:center;">${attendees.includes(p.member_name) ? 'Present' : 'Absent'}</td><td></td></tr>`
  ).join('');
  const headers = isBM
    ? ['Director', 'DIN', 'Mode', 'Status', 'Signature']
    : ['Member', 'Folio No. / DP-ID', 'No. of Shares', 'Status', 'Signature'];
  return `
    <h1>${companyName}</h1>
    <h2>Attendance Sheet — ${meetingLabel(type)}</h2>
    <table><tbody>
      <tr><td>Date</td><td>${md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN') : '—'}</td></tr>
      <tr><td>Venue</td><td>${md.venue || '—'}</td></tr>
    </tbody></table>
    <table style="margin-top:14pt;"><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows || `<tr><td colspan="${headers.length}">No roster on record.</td></tr>`}</tbody></table>
    ${!isBM ? '<p style="margin-top:10pt;font-size:9pt;">Proxies strictly prohibit substitution at Board Meetings; this sheet records members/authorized representatives attending in person or by proxy (Form MGT-11).</p>' : ''}
  `;
}

// ── CTC ───────────────────────────────────────────────────────────────
export function buildCtcHtml({ companyName, type, md, chosen, extras }) {
  const extrasSafe = extras || {};
  const signatoryName = extrasSafe.signatory_name || md.chairperson || '';
  const signatoryRole = extrasSafe.signatory_role || 'Director';
  return `
    <h1>${companyName}</h1>
    <h2>Certified True Copy of Resolution${chosen.length > 1 ? 's' : ''}</h2>
    <p>Extract of Minutes of the ${meetingLabel(type)} held on ${md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN') : '__________'}${md.venue ? ` at ${md.venue}` : ''}</p>
    ${chosen.map(i => `
      <p class="clause-heading" style="margin-top:14pt;">ITEM NO. ${i.itemNo}: ${i.resolution.resolution_title.toUpperCase()}</p>
      ${i.isFinal ? textToHtml(i.text) : '<p><em>Not yet finalized.</em></p>'}
      ${type !== 'BM' && i.resolution.explanatory_statement?.trim() ? `<p style="margin-top:6pt;"><em>Explanatory Statement summary:</em> ${i.resolution.explanatory_statement.trim()}</p>` : ''}
    `).join('')}
    <p style="margin-top:24pt;">Certified to be a true copy.</p>
    <table><tbody><tr class="sig-row"><td style="width:50%;">For ${companyName}<br/><br/><br/>_____________________<br/>${signatoryName ? `${signatoryName}<br/>` : ''}${signatoryRole}</td><td style="width:50%;">Place: <br/>Date: </td></tr></tbody></table>
  `;
}

// ── SHORTER NOTICE CONSENT — BM checks Independent Director
// presence/ratification (no shareholding math); GM totals consenting
// shareholding against the statutory 95% threshold. ────────────────────
export function buildShorterConsentHtml({ companyName, type, md, directors, shareholders, consenters }) {
  const isBM = type === 'BM';
  if (isBM) {
    const ids = (directors || []).filter(d => d.is_independent_director);
    const idPresent = ids.some(d => consenters.includes(d.director_name));
    const idsExist = ids.length > 0;
    const statusLine = !idsExist
      ? 'The Company has no Independent Directors on its Board; consent to shorter notice was accorded in the ordinary course.'
      : idPresent
        ? `At least one Independent Director (${ids.filter(d => consenters.includes(d.director_name)).map(d => d.director_name).join(', ')}) was present and participated in the meeting held on shorter notice.`
        : 'No Independent Director was present at this meeting held on shorter notice. Accordingly, the resolution(s) passed shall be circulated to all Directors and shall stand ratified only upon confirmation by at least one Independent Director, as required under SS-1 Para 1.3.11.';
    return `
      <h1>${companyName}</h1>
      <h2>Consent for Shorter Notice — Board Meeting</h2>
      <table><tbody>
        <tr><td>Meeting Date</td><td>${md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN') : '—'}</td></tr>
        <tr><td>Notice Sent</td><td>${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : '—'}</td></tr>
      </tbody></table>
      <p style="margin-top:14pt;">We, the undersigned Director(s), hereby consent to the meeting being held at shorter notice than the seven days otherwise required under Section 173(3) of the Companies Act, 2013.</p>
      <p style="margin-top:8pt;">${statusLine}</p>
      <table style="margin-top:14pt;"><thead><tr><th>Director</th><th>Consented</th><th>Signature</th></tr></thead><tbody>
        ${(directors || []).map(d => `<tr><td>${d.director_name}${d.is_independent_director ? ' (ID)' : ''}</td><td style="text-align:center;">${consenters.includes(d.director_name) ? 'Yes' : '—'}</td><td></td></tr>`).join('') || '<tr><td colspan="3">No directors on record.</td></tr>'}
      </tbody></table>
    `;
  }

  const totalMemberWeight = (shareholders || []).reduce((sum, s) => sum + (Number(s.no_of_shares) || 0), 0);
  const consentWeight = consenters.reduce((sum, name) => {
    const sh = (shareholders || []).find(s => s.member_name === name);
    return sum + (sh ? Number(sh.no_of_shares) || 0 : 0);
  }, 0);
  const pct = totalMemberWeight > 0 ? (consentWeight / totalMemberWeight) * 100 : 0;
  const meets95 = pct >= 95;
  return `
    <h1>${companyName}</h1>
    <h2>Consent for Shorter Notice — ${meetingLabel(type)}</h2>
    <table><tbody>
      <tr><td>Meeting Date</td><td>${md.meeting_date ? new Date(md.meeting_date).toLocaleDateString('en-IN') : '—'}</td></tr>
      <tr><td>Notice Sent</td><td>${md.notice_date ? new Date(md.notice_date).toLocaleDateString('en-IN') : '—'}</td></tr>
    </tbody></table>
    <p style="margin-top:14pt;">We, the undersigned Members, hereby consent to the meeting being held at shorter notice than the twenty-one clear days otherwise required under Section 101(1) of the Companies Act, 2013.</p>
    <table style="margin-top:14pt;"><thead><tr><th>Member</th><th>No. of Shares</th><th>Consented</th><th>Signature</th></tr></thead><tbody>
      ${(shareholders || []).map(s => `<tr><td>${s.member_name}</td><td style="text-align:center;">${s.no_of_shares || 0}</td><td style="text-align:center;">${consenters.includes(s.member_name) ? 'Yes' : '—'}</td><td></td></tr>`).join('') || '<tr><td colspan="4">No members on record.</td></tr>'}
    </tbody></table>
    <p style="margin-top:14pt; font-weight:bold;">Consenting shareholding: ${consentWeight} of ${totalMemberWeight} shares (${pct.toFixed(2)}%) — ${meets95 ? 'meets' : 'does NOT meet'} the statutory 95% threshold under Section 101(1).</p>
  `;
}
